import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bitcoin, Loader2, AlertTriangle, Copy as CopyIcon, CheckCircle2, MessageCircle } from 'lucide-react';
import { api } from '../api/client';
import { useSite } from '../context/SiteContext';
import { toast } from 'sonner';

const COIN_LABELS = {
  BTC: 'Bitcoin (BTC)',
  ETH: 'Ethereum (ETH)',
  LTC: 'Litecoin (LTC)',
  USDT: 'Tether (USDT)',
};

const OrderCryptoPay = () => {
  const { orderNumber } = useParams();
  const { site } = useSite();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getOrder(orderNumber)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success('Copied'); } catch { toast.error('Copy failed'); }
  };

  if (loading) return (<><Header /><main className="bg-black min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 text-[#d4af37] animate-spin" /></main><Footer /></>);
  if (!order) return (
    <><Header />
      <main className="bg-black min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-10 w-10 text-rose-400 mx-auto mb-3" />
          <h1 className="serif-italic gold-gradient-text text-4xl">Order not found</h1>
          <Link to="/" className="text-[#d4af37] mt-4 inline-block underline">Back to home</Link>
        </div>
      </main>
      <Footer />
    </>
  );

  const coin = order.crypto_settle || 'BTC';
  const wallet = order.crypto_wallet || '';
  const discountApplied = order.crypto_discount || 0;
  const whatsapp = (site?.socials?.whatsapp || site?.contact?.phone || '').replace(/[^\d+]/g, '');

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        <div className="max-w-[860px] mx-auto px-6 py-16">
          <div className="text-center mb-8">
            <Bitcoin className="h-14 w-14 mx-auto mb-3 text-[#d4af37]" />
            <p className="text-[#d4af37] tracking-[0.3em] text-xs uppercase mb-2">Crypto payment</p>
            <h1 className="serif-italic gold-gradient-text text-5xl" data-testid="crypto-order-number">{order.order_number}</h1>
            {discountApplied > 0 && (
              <p className="text-emerald-400 mt-3 text-sm" data-testid="crypto-discount-applied">
                <CheckCircle2 className="inline h-4 w-4 mr-1" /> 15% crypto discount applied: −€{discountApplied.toFixed(2)}
              </p>
            )}
          </div>

          <Card className="bg-zinc-950 border-[#d4af37]/30 p-6 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <p className="text-zinc-400 text-xs">Send exactly</p>
                <p className="text-[#d4af37] text-3xl serif-italic" data-testid="crypto-total">€{order.total.toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">worth in {COIN_LABELS[coin] || coin}</p>
              </div>
              <Badge variant="outline" className="border-amber-500 text-amber-400 text-sm px-3 py-1.5">Awaiting payment</Badge>
            </div>
            <div>
              <p className="text-zinc-300 text-sm mb-1">To this wallet address:</p>
              {wallet ? (
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 bg-black border border-zinc-800 rounded px-3 py-2 text-[#d4af37] text-xs md:text-sm break-all font-mono" data-testid="crypto-wallet">{wallet}</code>
                  <Button onClick={() => copy(wallet)} variant="outline" className="btn-outline-gold shrink-0" data-testid="crypto-copy-wallet"><CopyIcon className="h-4 w-4" /></Button>
                </div>
              ) : (
                <p className="text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-3 text-sm" data-testid="crypto-wallet-missing">
                  Wallet address for {coin} is not configured yet. Our team will contact you shortly with wallet details.
                </p>
              )}
              <p className="text-zinc-500 text-xs mt-2">
                Convert €{order.total.toFixed(2)} to {coin} at current market rate before sending.
                Once we detect the transaction on-chain we ship your order — usually within a few hours.
              </p>
            </div>
          </Card>

          {whatsapp && (
            <Card className="bg-zinc-950 border-zinc-900 p-5 mb-5">
              <div className="flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-[#d4af37] shrink-0" />
                <div className="flex-1">
                  <p className="text-zinc-100 text-sm font-medium">Need help sending?</p>
                  <p className="text-zinc-500 text-xs">Message us on WhatsApp — we&rsquo;ll guide you through the transaction step by step.</p>
                </div>
                <a href={`https://wa.me/${whatsapp.replace('+','')}?text=${encodeURIComponent(`Hi, I placed order ${order.order_number}. Please guide me through the ${coin} payment.`)}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold h-10">Open WhatsApp</Button>
                </a>
              </div>
            </Card>
          )}

          <Card className="bg-zinc-950 border-zinc-900 p-5">
            <h3 className="serif-italic text-lg text-[#d4af37] mb-3">Order summary</h3>
            <ul className="space-y-1.5 text-sm text-zinc-300">
              {order.items.map((i, idx) => (
                <li key={`${i.product_id || 'item'}-${idx}`} className="flex justify-between"><span>{i.name} × {i.qty}</span><span>€{(i.price * i.qty).toFixed(2)}</span></li>
              ))}
            </ul>
            <div className="border-t border-zinc-800 mt-3 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>€{order.subtotal.toFixed(2)}</span></div>
              {discountApplied > 0 && <div className="flex justify-between text-emerald-400"><span>Crypto discount (15%)</span><span>−€{discountApplied.toFixed(2)}</span></div>}
              {order.shipping_cost > 0 && <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>€{order.shipping_cost.toFixed(2)}</span></div>}
              <div className="flex justify-between text-zinc-100 pt-2 border-t border-zinc-800 mt-2">
                <span className="text-base">Total</span>
                <span className="text-[#d4af37] text-xl">€{order.total.toFixed(2)}</span>
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default OrderCryptoPay;
